import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";

import "../../../assets/css/AGGrid-custom.css";
import { CustomMultiSelect } from "../../../components/common/inputFields/customMultiSelect";
import { CustomButton } from "../../../components/common/customButton";
import { ErrorDialog } from "../../../components/common/errorDialog";
import { useErrorDialog } from "../../../hooks/useErrorDialog";
import { useReferenceLists } from "../../../context/GlobalGetLists";
import type { Status } from "../../../types/deal";
import type {
  RoleAllocation,
  StatusBreakdown,
  UserAllocation,
  UserAllocationDeal,
  UserAllocationRole,
  UserAllocationsResponse,
} from "../../../types/userAllocations";
import {
  activeStatusIdsExcludingClosed,
  getUserAllocationDeals,
  getUserAllocations,
  type DealsActiveFilter,
} from "../../userAllocations/userAllocationsApi";
import { SummaryBarChart } from "./SummaryBarChart";
import { UserAllocationCombinationsSection } from "./UserAllocationCombinationsSection";
import { CombinationStatusBreakdownTable } from "./CombinationStatusBreakdownTable";
import type { CombinationSelection } from "./combinationUtils";
import {
  pruneStatusFilter,
  statusOptionsForDealsActive,
} from "./userAllocationsStatusFilter";
import { getColorForName, getInitials } from "../../../utils/noteUtils";

ModuleRegistry.registerModules([AllCommunityModule]);

const formatGbp = (value: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatGbpWhole = (value: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const formatGbpMm = (value: number) => {
  const mm = value / 1_000_000;
  return `£${mm.toLocaleString("en-GB", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}`;
};

const formatCount = (value: number) => value.toLocaleString("en-GB");

function chartScopeLabel(dealsActive: DealsActiveFilter): string {
  if (dealsActive === "active" || dealsActive === "inactive") {
    return `${dealsActive} `;
  }
  return "";
}

const formatProbability = (value: number | null) =>
  value == null ? "—" : `${value}%`;

const PRIORITY_COLORS: Record<string, string> = {
  High: "#4caf50",
  Medium: "#ffb300",
  Low: "#f44336",
};

/** Matches `--ag-row-hover-color` in AGGrid-custom.css (pipeline grid). */
const PIPELINE_ROW_HOVER_COLOR = "#e1efea";

const UNASSIGNED_LEAD_COLOR = "#0E4664";
const UNASSIGNED_SUPPORT_COLOR = "#1B7A63";

function UnassignedRoleStat({
  label,
  role,
  allocation,
}: {
  label: string;
  role: UserAllocationRole;
  allocation: RoleAllocation;
}) {
  const accentColor =
    role === "deal_lead" ? UNASSIGNED_LEAD_COLOR : UNASSIGNED_SUPPORT_COLOR;

  return (
    <Box
      sx={{
        border: "1px solid #e8eaed",
        borderLeft: `4px solid ${accentColor}`,
        borderRadius: "6px",
        bgcolor: "#fafbfc",
        px: 1.75,
        py: 1.5,
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: PIPELINE_ROW_HOVER_COLOR },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: accentColor,
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: "#182737",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#182737", lineHeight: 1.2 }}>
        {formatCount(allocation.deal_count)}
        <Typography
          component="span"
          sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary", ml: 0.75 }}
        >
          deals
        </Typography>
      </Typography>
      <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
        {formatGbpWhole(allocation.loan_amount_gbp)}
      </Typography>
    </Box>
  );
}

function UnassignedDealsCard({
  dealLead,
  dealSupport,
  onViewBreakdown,
}: {
  dealLead: RoleAllocation;
  dealSupport: RoleAllocation;
  onViewBreakdown: () => void;
}) {
  const totalDeals = dealLead.deal_count + dealSupport.deal_count;
  const totalVolume = dealLead.loan_amount_gbp + dealSupport.loan_amount_gbp;

  return (
    <Box
      sx={{
        border: "1px solid #e0e4e8",
        borderRadius: "8px",
        bgcolor: "#fff",
        p: 2,
        boxShadow: "0 1px 3px rgba(24, 39, 55, 0.08)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              bgcolor: "#fff4e5",
              color: "#e65100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PersonOffOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "16px !important", fontWeight: "400 !important", color: "#182737 !important", mb: 0.25 }}>
              Unassigned deals
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.4 }}>
              Deals without a deal lead or deal support assigned.
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#182737", mt: 0.75, fontWeight: 500 }}>
              {formatCount(totalDeals)} deals total · {formatGbpWhole(totalVolume)}
            </Typography>
          </Box>
        </Box>
        <CustomButton
          handleButtonPress={onViewBreakdown}
          isButtonDisabled={false}
          text="View breakdown"
          solid={false}
          heightOverride="32px"
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: dealLead.deal_count > 0 && dealSupport.deal_count > 0 ? "1fr 1fr" : "1fr",
          },
          gap: 1.5,
        }}
      >
        {dealLead.deal_count > 0 && (
          <UnassignedRoleStat
            label="Deal lead"
            role="deal_lead"
            allocation={dealLead}
          />
        )}
        {dealSupport.deal_count > 0 && (
          <UnassignedRoleStat
            label="Deal support"
            role="deal_support"
            allocation={dealSupport}
          />
        )}
      </Box>
    </Box>
  );
}

function statusRowKey(row: StatusBreakdown) {
  return `${row.id ?? "null"}-${row.name}-${row.sort_order}`;
}

function getCounterpartName(
  deal: UserAllocationDeal,
  role: UserAllocationRole,
): string | null {
  return role === "deal_lead"
    ? deal.deal_support_name ?? null
    : deal.deal_lead_name ?? null;
}

function getCounterpartColumnLabel(role: UserAllocationRole): string {
  return role === "deal_lead" ? "Support" : "Lead";
}

function AssigneeAvatar({ name }: { name: string | null }) {
  if (!name) {
    return <>—</>;
  }

  return (
    <Tooltip title={name} arrow>
      <Avatar
        sx={{
          width: 28,
          height: 28,
          fontSize: "0.65rem",
          fontWeight: 700,
          bgcolor: getColorForName(name),
        }}
      >
        {getInitials(name)}
      </Avatar>
    </Tooltip>
  );
}

const expandedDealsTableSx = {
  tableLayout: "fixed",
  width: "100%",
  "& .MuiTableCell-root": {
    fontSize: 13,
    px: 1.25,
    py: 0.875,
    lineHeight: 1.3,
  },
  "& .MuiTableCell-head": {
    fontWeight: 600,
    fontSize: 12,
    color: "#182737",
    whiteSpace: "nowrap",
  },
} as const;

function expandedDealColWidths(unassigned: boolean) {
  if (unassigned) {
    return {
      deal: "34%",
      lead: "10%",
      support: "10%",
      priority: "14%",
      probability: "13%",
      amount: "19%",
    };
  }
  return {
    deal: "38%",
    counterpart: "11%",
    priority: "15%",
    probability: "14%",
    amount: "22%",
  };
}

function buildDealsCacheKey(args: {
  userId?: number;
  unassigned: boolean;
  role: UserAllocationRole;
  statusRow: StatusBreakdown;
  dealsActive: DealsActiveFilter;
  statusIds?: number[];
}) {
  const { userId, unassigned, role, statusRow, dealsActive, statusIds } = args;
  const subject = unassigned ? "unassigned" : String(userId);
  const status = statusRow.id ?? "null";
  const statusFilterKey = statusIds?.slice().sort((a, b) => a - b).join(",") ?? "";
  return `${subject}|${role}|${status}|${dealsActive}|${statusFilterKey}`;
}

function StatusBreakdownTable({
  rows,
  role,
  userId,
  unassigned = false,
  dealsActive,
  statusIds,
  cacheResetKey,
  onFetchError,
}: {
  rows: StatusBreakdown[];
  role: UserAllocationRole;
  userId?: number;
  unassigned?: boolean;
  dealsActive: DealsActiveFilter;
  statusIds?: number[];
  cacheResetKey: string;
  onFetchError: (message: string) => void;
}) {
  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string>>(() => new Set());
  const [dealsCache, setDealsCache] = useState<Map<string, UserAllocationDeal[]>>(
    () => new Map(),
  );
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(() => new Set());
  const dealsCacheRef = useRef(dealsCache);
  dealsCacheRef.current = dealsCache;

  useEffect(() => {
    setExpandedRowKeys(new Set());
    setDealsCache(new Map());
    setLoadingKeys(new Set());
  }, [cacheResetKey]);

  const fetchDeals = useCallback(
    async (row: StatusBreakdown, cacheKey: string) => {
      setLoadingKeys((prev) => new Set(prev).add(cacheKey));
      try {
        const response = await getUserAllocationDeals({
          role,
          dealsActive,
          statusIds,
          ...(unassigned ? { unassigned: true } : { userId }),
          ...(row.id == null
            ? { statusUnassigned: true }
            : { statusId: row.id }),
        });

        if (response.success && Array.isArray(response.data)) {
          setDealsCache((prev) => new Map(prev).set(cacheKey, response.data!));
          return;
        }

        onFetchError("Failed to load deals for this status.");
        setDealsCache((prev) => new Map(prev).set(cacheKey, []));
      } catch {
        onFetchError("Failed to load deals for this status. Please try again.");
        setDealsCache((prev) => new Map(prev).set(cacheKey, []));
      } finally {
        setLoadingKeys((prev) => {
          if (!prev.has(cacheKey)) return prev;
          const next = new Set(prev);
          next.delete(cacheKey);
          return next;
        });
      }
    },
    [dealsActive, onFetchError, role, statusIds, unassigned, userId],
  );

  const handleToggle = useCallback(
    (row: StatusBreakdown) => {
      if (row.deal_count <= 0) return;

      const rowKey = statusRowKey(row);
      const cacheKey = buildDealsCacheKey({
        userId,
        unassigned,
        role,
        statusRow: row,
        dealsActive,
        statusIds,
      });

      const isExpanding = !expandedRowKeys.has(rowKey);

      setExpandedRowKeys((prev) => {
        const next = new Set(prev);
        if (isExpanding) {
          next.add(rowKey);
        } else {
          next.delete(rowKey);
        }
        return next;
      });

      if (isExpanding && !dealsCacheRef.current.has(cacheKey)) {
        void fetchDeals(row, cacheKey);
      }
    },
    [dealsActive, expandedRowKeys, fetchDeals, role, statusIds, unassigned, userId],
  );

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No deals in this role for current filters.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ tableLayout: "fixed", width: "100%" }}
      >
        <TableHead>
          <TableRow>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 20, flexShrink: 0 }} aria-hidden />
                Status
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ width: 72 }}>
              Deals
            </TableCell>
            <TableCell align="right" sx={{ width: 128 }}>
              Volume (£)
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const rowKey = statusRowKey(row);
            const isExpanded = expandedRowKeys.has(rowKey);
            const canExpand = row.deal_count > 0;
            const cacheKey = buildDealsCacheKey({
              userId,
              unassigned,
              role,
              statusRow: row,
              dealsActive,
              statusIds,
            });
            const deals = dealsCache.get(cacheKey);
            const isLoading = loadingKeys.has(cacheKey);

            return (
              <Fragment key={rowKey}>
                <TableRow
                  hover={false}
                  onClick={() => handleToggle(row)}
                  sx={{
                    cursor: canExpand ? "pointer" : "default",
                    ...(canExpand && {
                      "&:hover": { backgroundColor: PIPELINE_ROW_HOVER_COLOR },
                    }),
                    "& > *": {
                      borderBottom: isExpanded ? "none" : undefined,
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: isExpanded ? 700 : undefined }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontWeight: isExpanded ? 700 : undefined,
                      }}
                    >
                      {canExpand ? (
                        isExpanded ? (
                          <ExpandLessIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        ) : (
                          <ExpandMoreIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        )
                      ) : (
                        <Box sx={{ width: 20 }} />
                      )}
                      {row.name}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ width: 72, fontWeight: isExpanded ? 700 : undefined }}
                  >
                    {formatCount(row.deal_count)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ width: 128, fontWeight: isExpanded ? 700 : undefined }}
                  >
                    {formatGbp(row.loan_amount_gbp)}
                  </TableCell>
                </TableRow>
                {canExpand && (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{ py: 0, px: 0, borderBottom: isExpanded ? undefined : "none" }}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 1, py: 1, bgcolor: "#f8f9fa" }}>
                          {isLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                              <CircularProgress size={22} />
                            </Box>
                          ) : deals && deals.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                              No deals found.
                            </Typography>
                          ) : deals ? (
                            (() => {
                              const col = expandedDealColWidths(unassigned);
                              return (
                            <Table size="small" sx={expandedDealsTableSx}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ width: col.deal }}>Deal</TableCell>
                                  {unassigned ? (
                                    <>
                                      <TableCell align="center" sx={{ width: col.lead }}>
                                        Lead
                                      </TableCell>
                                      <TableCell align="center" sx={{ width: col.support }}>
                                        Support
                                      </TableCell>
                                    </>
                                  ) : (
                                    <TableCell align="center" sx={{ width: col.counterpart }}>
                                      {getCounterpartColumnLabel(role)}
                                    </TableCell>
                                  )}
                                  <TableCell align="center" sx={{ width: col.priority }}>
                                    Priority
                                  </TableCell>
                                  <TableCell align="center" sx={{ width: col.probability }}>
                                    Probability
                                  </TableCell>
                                  <TableCell align="right" sx={{ width: col.amount }}>
                                    Amount (£)
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {deals.map((deal) => {
                                  const counterpartName = getCounterpartName(deal, role);

                                  return (
                                  <TableRow key={deal.id}>
                                    <TableCell sx={{ width: col.deal, maxWidth: col.deal }}>
                                      <Typography
                                        component="a"
                                        href={`/deals/${deal.id}`}
                                        title={deal.deal_name || `ID ${deal.id}`}
                                        sx={{
                                          color: "#1B7A63",
                                          textDecoration: "underline",
                                          display: "block",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          fontSize: 13,
                                        }}
                                      >
                                        {deal.deal_name || `ID ${deal.id}`}
                                      </Typography>
                                    </TableCell>
                                    {unassigned ? (
                                      <>
                                        <TableCell align="center" sx={{ width: col.lead }}>
                                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                                            <AssigneeAvatar name={deal.deal_lead_name ?? null} />
                                          </Box>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: col.support }}>
                                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                                            <AssigneeAvatar name={deal.deal_support_name ?? null} />
                                          </Box>
                                        </TableCell>
                                      </>
                                    ) : (
                                      <TableCell align="center" sx={{ width: col.counterpart }}>
                                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                                          <AssigneeAvatar name={counterpartName} />
                                        </Box>
                                      </TableCell>
                                    )}
                                    <TableCell align="center" sx={{ width: col.priority }}>
                                      {deal.priority ? (
                                        <Typography
                                          sx={{
                                            color: PRIORITY_COLORS[deal.priority] ?? "text.primary",
                                            fontWeight: 500,
                                            textAlign: "center",
                                            fontSize: 13,
                                          }}
                                        >
                                          {deal.priority}
                                        </Typography>
                                      ) : (
                                        "—"
                                      )}
                                    </TableCell>
                                    <TableCell align="center" sx={{ width: col.probability }}>
                                      {formatProbability(deal.probability)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: col.amount }}>
                                      {formatGbp(deal.loan_amount_gbp)}
                                    </TableCell>
                                  </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                              );
                            })()
                          ) : null}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RoleBreakdownSection({
  title,
  role,
  roleKey,
  userId,
  unassigned = false,
  dealsActive,
  statusIds,
  cacheResetKey,
  onFetchError,
}: {
  title: string;
  role: RoleAllocation;
  roleKey: UserAllocationRole;
  userId?: number;
  unassigned?: boolean;
  dealsActive: DealsActiveFilter;
  statusIds?: number[];
  cacheResetKey: string;
  onFetchError: (message: string) => void;
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          fontSize: "14px !important",
          fontWeight: "700 !important",
          color: "#182737 !important",
        }}
      >
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {formatCount(role.deal_count)} deals · {formatGbp(role.loan_amount_gbp)}
      </Typography>
      <StatusBreakdownTable
        rows={role.by_status}
        role={roleKey}
        userId={userId}
        unassigned={unassigned}
        dealsActive={dealsActive}
        statusIds={statusIds}
        cacheResetKey={cacheResetKey}
        onFetchError={onFetchError}
      />
    </Box>
  );
}

export function UserAllocationsTab() {
  const { status: allStatuses } = useReferenceLists();
  const [dealsActive, setDealsActive] = useState<DealsActiveFilter>("active");
  const [statusFilter, setStatusFilter] = useState<Status[]>([]);
  const [data, setData] = useState<UserAllocationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserAllocation | null>(null);
  const [selectedCombination, setSelectedCombination] =
    useState<CombinationSelection | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<
    "user" | "unassigned" | "combination"
  >("user");
  const [drawerSessionKey, setDrawerSessionKey] = useState(0);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  const fetchStatusIds = useMemo(() => {
    if (statusFilter.length > 0) {
      return statusFilter.map((status) => status.id);
    }
    if (dealsActive === "active") {
      return activeStatusIdsExcludingClosed(allStatuses);
    }
    return undefined;
  }, [statusFilter, dealsActive, allStatuses]);

  const statusOptions = useMemo(
    () => statusOptionsForDealsActive(allStatuses, dealsActive),
    [allStatuses, dealsActive],
  );

  const chartTitles = useMemo(
    () => {
      const scope = chartScopeLabel(dealsActive);
      return {
        deals: `Total ${scope}deals by user`,
        volume: `Total ${scope}volume by user (£MM)`,
      };
    },
    [dealsActive],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    getUserAllocations({ dealsActive, statusIds: fetchStatusIds })
      .then((response) => {
        if (cancelled) return;
        if (response.success && response.data) {
          setData(response.data);
          return;
        }
        showErrorFromResponse(response);
        setData(null);
        setLoadError("Failed to load user allocations.");
      })
      .catch(() => {
        if (cancelled) return;
        showErrorFromResponse({
          success: false,
          status: 500,
          message: "Failed to fetch user allocations. Please try again.",
        });
        setData(null);
        setLoadError("Failed to load user allocations.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealsActive, fetchStatusIds]);

  const rows = data?.allocations ?? [];

  const dealsChartItems = useMemo(
    () =>
      rows.map((row) => ({
        name: row.name,
        value: row.combined.deal_count,
      })),
    [rows],
  );

  const volumeChartItems = useMemo(
    () =>
      rows.map((row) => ({
        name: row.name,
        value: row.combined.loan_amount_gbp,
      })),
    [rows],
  );

  const columnDefs = useMemo<ColDef<UserAllocation>[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        width: 180,
        pinned: "left",
      },
      {
        colId: "totalDeals",
        headerName: "Total deals",
        headerTooltip:
          "Lead assignments plus support assignments. The same deal can count in both roles.",
        width: 110,
        valueGetter: (params) => params.data?.combined.deal_count ?? 0,
        cellStyle: { textAlign: "right" },
      },
      {
        colId: "totalVolume",
        headerName: "Total volume (£)",
        width: 140,
        valueGetter: (params) => params.data?.combined.loan_amount_gbp ?? 0,
        valueFormatter: (params) => formatGbp(Number(params.value ?? 0)),
        cellStyle: { textAlign: "right" },
      },
      {
        colId: "leadDeals",
        headerName: "Lead deals",
        width: 100,
        valueGetter: (params) => params.data?.deal_lead.deal_count ?? 0,
        cellStyle: { textAlign: "right" },
      },
      {
        colId: "leadVolume",
        headerName: "Lead volume (£)",
        width: 130,
        valueGetter: (params) => params.data?.deal_lead.loan_amount_gbp ?? 0,
        valueFormatter: (params) => formatGbp(Number(params.value ?? 0)),
        cellStyle: { textAlign: "right" },
      },
      {
        colId: "supportDeals",
        headerName: "Support deals",
        width: 110,
        valueGetter: (params) => params.data?.deal_support.deal_count ?? 0,
        cellStyle: { textAlign: "right" },
      },
      {
        colId: "supportVolume",
        headerName: "Support volume (£)",
        width: 140,
        valueGetter: (params) => params.data?.deal_support.loan_amount_gbp ?? 0,
        valueFormatter: (params) => formatGbp(Number(params.value ?? 0)),
        cellStyle: { textAlign: "right" },
      },
    ],
    [],
  );

  const handleRowClicked = useCallback((event: RowClickedEvent<UserAllocation>) => {
    if (!event.data) return;
    setSelectedUser(event.data);
    setSelectedCombination(null);
    setDrawerMode("user");
    setDrawerOpen(true);
  }, []);

  const handleSelectCombination = useCallback(
    (selection: CombinationSelection) => {
      setSelectedCombination(selection);
      setSelectedUser(null);
      setDrawerMode("combination");
      setDrawerOpen(true);
    },
    [],
  );

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setDrawerSessionKey((key) => key + 1);
  }, []);

  const handleDrillFetchError = useCallback(
    (message: string) => {
      showErrorFromResponse({
        success: false,
        status: 500,
        message,
      });
    },
    [showErrorFromResponse],
  );

  const drawerCacheResetKey = useMemo(() => {
    const statusKey = fetchStatusIds?.slice().sort((a, b) => a - b).join(",") ?? "";
    let subject = "";
    if (drawerMode === "unassigned") {
      subject = "unassigned";
    } else if (drawerMode === "combination" && selectedCombination) {
      const lead = selectedCombination.leadUnassigned
        ? "u"
        : String(selectedCombination.leadUserId ?? "?");
      const support = selectedCombination.supportUnassigned
        ? "u"
        : String(selectedCombination.supportUserId ?? "?");
      subject = `${lead}->${support}`;
    } else if (selectedUser) {
      subject = String(selectedUser.user_id);
    }
    return `${drawerSessionKey}|${drawerMode}|${subject}|${dealsActive}|${statusKey}`;
  }, [
    dealsActive,
    drawerMode,
    drawerSessionKey,
    fetchStatusIds,
    selectedCombination,
    selectedUser,
  ]);

  const getRowStyle = useCallback(
    (params: { data?: UserAllocation }) => {
      if (selectedUser && params.data?.user_id === selectedUser.user_id) {
        return { backgroundColor: "#e8f5f0" };
      }
      return undefined;
    },
    [selectedUser],
  );

  const hasUnassigned =
    (data?.unassigned.deal_lead.deal_count ?? 0) > 0 ||
    (data?.unassigned.deal_support.deal_count ?? 0) > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        width: "100%",
        minWidth: 0,
        pt: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={dealsActive}
          onChange={(_, value: DealsActiveFilter | null) => {
            if (!value) return;
            setDealsActive(value);
            setStatusFilter((prev) =>
              pruneStatusFilter(
                prev,
                statusOptionsForDealsActive(allStatuses, value),
              ),
            );
          }}
          aria-label="Deal activity filter"
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        <CustomMultiSelect
          value={statusFilter.map((status) => status.id)}
          onChange={(event) => {
            const selectedIds = event.target.value as (string | number)[];
            setStatusFilter(
              statusOptions.filter((option: Status) =>
                selectedIds.includes(option.id),
              ),
            );
          }}
          options={statusOptions}
          placeholder="Select Status"
          labelKey="status"
          minWidth="200px"
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Box sx={{ py: 4, color: "text.secondary" }} className="text-general-page-text">
          {loadError}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%", minWidth: 0 }}>
          {rows.length === 0 && !hasUnassigned ? (
            <Box sx={{ py: 4, color: "text.secondary" }} className="text-general-page-text">
              No allocations match the current filters.
            </Box>
          ) : (
            <>
              <Paper
                elevation={0}
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  background:
                    "linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 28%, #FFFFFF 100%)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: { xs: 2, md: 2.5 },
                    py: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 2,
                    flexShrink: 0,
                  }}
                >
                  <Typography className="text-general-section-title">
                    User allocations
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    <Box
                      component="span"
                      sx={{ fontWeight: 700, color: "#182737" }}
                    >
                      {rows.length}
                    </Box>{" "}
                    users
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: { xs: 1.5, md: 2 },
                    py: 1.5,
                    overflow: "auto",
                  }}
                >
                  <div className="ag-theme-alpine" style={{ width: "100%" }}>
                    <AgGridReact<UserAllocation>
                      rowData={rows}
                      columnDefs={columnDefs}
                      defaultColDef={{
                        flex: 0,
                        resizable: true,
                        filter: false,
                        sortable: true,
                      }}
                      rowHeight={30}
                      headerHeight={30}
                      suppressCellFocus
                      domLayout="autoHeight"
                      onRowClicked={handleRowClicked}
                      getRowStyle={getRowStyle}
                    />
                  </div>
                </Box>
              </Paper>

              {rows.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 2,
                    alignItems: "stretch",
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
                    <SummaryBarChart
                      title={chartTitles.deals}
                      items={dealsChartItems}
                      formatValue={(value) => formatCount(value)}
                      minHeight={280}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
                    <SummaryBarChart
                      title={chartTitles.volume}
                      items={volumeChartItems}
                      formatValue={formatGbpMm}
                      minHeight={280}
                    />
                  </Box>
                </Box>
              )}

              {hasUnassigned && data && (
                <UnassignedDealsCard
                  dealLead={data.unassigned.deal_lead}
                  dealSupport={data.unassigned.deal_support}
                  onViewBreakdown={() => {
                    setSelectedUser(null);
                    setSelectedCombination(null);
                    setDrawerMode("unassigned");
                    setDrawerOpen(true);
                  }}
                />
              )}
            </>
          )}

          <UserAllocationCombinationsSection
            dealsActive={dealsActive}
            fetchStatusIds={fetchStatusIds}
            onSelectCombination={handleSelectCombination}
            onFetchError={handleDrillFetchError}
          />
        </Box>
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 680 },
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box sx={{ pr: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "22px !important",
                fontWeight: "700 !important",
                color: "#182737 !important",
                lineHeight: "1.15 !important",
                wordBreak: "break-word",
              }}
            >
              {drawerMode === "unassigned"
                ? "Unassigned"
                : drawerMode === "combination" && selectedCombination
                  ? `${selectedCombination.leadName} → ${selectedCombination.supportName}`
                  : selectedUser?.name ?? "—"}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={handleCloseDrawer}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, overflowY: "auto", flex: 1, minHeight: 0, scrollbarGutter: "stable" }}>
          {drawerMode === "user" && selectedUser && (
            <>
              <RoleBreakdownSection
                title="Deal Lead"
                role={selectedUser.deal_lead}
                roleKey="deal_lead"
                userId={selectedUser.user_id}
                dealsActive={dealsActive}
                statusIds={fetchStatusIds}
                cacheResetKey={drawerCacheResetKey}
                onFetchError={handleDrillFetchError}
              />
              <RoleBreakdownSection
                title="Deal Support"
                role={selectedUser.deal_support}
                roleKey="deal_support"
                userId={selectedUser.user_id}
                dealsActive={dealsActive}
                statusIds={fetchStatusIds}
                cacheResetKey={drawerCacheResetKey}
                onFetchError={handleDrillFetchError}
              />
            </>
          )}
          {drawerMode === "unassigned" && data && (
            <>
              <RoleBreakdownSection
                title="Deal Lead"
                role={data.unassigned.deal_lead}
                roleKey="deal_lead"
                unassigned
                dealsActive={dealsActive}
                statusIds={fetchStatusIds}
                cacheResetKey={drawerCacheResetKey}
                onFetchError={handleDrillFetchError}
              />
              <RoleBreakdownSection
                title="Deal Support"
                role={data.unassigned.deal_support}
                roleKey="deal_support"
                unassigned
                dealsActive={dealsActive}
                statusIds={fetchStatusIds}
                cacheResetKey={drawerCacheResetKey}
                onFetchError={handleDrillFetchError}
              />
            </>
          )}
          {drawerMode === "combination" && selectedCombination && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontSize: "14px !important",
                  fontWeight: "700 !important",
                  color: "#182737 !important",
                }}
              >
                Status breakdown
              </Typography>
              <CombinationStatusBreakdownTable
                selection={selectedCombination}
                dealsActive={dealsActive}
                statusIds={fetchStatusIds}
                cacheResetKey={drawerCacheResetKey}
                onFetchError={handleDrillFetchError}
              />
            </Box>
          )}
        </Box>
      </Drawer>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
}
